import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  AppBar,
  Toolbar,
  Avatar,
  Chip,
  Badge,
  Divider,
  Alert,
  CircularProgress,
  Fab,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Settings as SettingsIcon,
  KeyboardArrowDown as ScrollDownIcon
} from '@mui/icons-material';
import { useChat } from '../hooks/useChat';
import MessageBubble from './MessageBubble';
import { WhatsAppMessage } from '../types';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { addNotification, useUpdateChatStatusMutation } from '../../../store';

interface ChatWindowProps {
  chatId: string;
  onSettingsClick?: () => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  chatId,
  onSettingsClick
}) => {
  const dispatch = useAppDispatch();
  const { 
    selectedChat, 
    messages, 
    isLoading, 
    isSending, 
    error, 
    sendMessage, 
    retryMessage,
    chatStats
  } = useChat(chatId);
  
  const selectedAgent = useAppSelector(state => state.agents.selectedAgent);
  const [updateChatStatus, { isLoading: isUpdatingStatus }] = useUpdateChatStatusMutation();
  
  // Local state
  const [messageInput, setMessageInput] = useState('');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isResumeDialogOpen, setIsResumeDialogOpen] = useState(false);
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll vers le bas pour nouveaux messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  // Gestion du scroll pour afficher/cacher le bouton de scroll
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Focus automatique sur l'input
  useEffect(() => {
    inputRef.current?.focus();
  }, [chatId]);

  const handleSendMessage = async () => {
    const content = messageInput.trim();
    if (!content || isSending) return;

    try {
      await sendMessage(content, 'text');
      setMessageInput('');
      
      // Refocus sur l'input après envoi
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error('Erreur envoi message:', error);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
    dispatch(addNotification({
      type: 'success',
      title: 'Copié',
      message: 'Message copié dans le presse-papier',
      duration: 2000
    }));
  };

  const handleRetryMessage = async (message: WhatsAppMessage) => {
    try {
      await retryMessage(message);
    } catch (error) {
      console.error('Erreur retry message:', error);
    }
  };

  const handleResumeAi = async () => {
    try {
      await updateChatStatus({ chatId, status: 'resume_ai' }).unwrap();
      setIsResumeDialogOpen(false);
      dispatch(addNotification({
        type: 'success',
        title: 'IA réactivée',
        message: 'L’assistant reprendra au prochain message du client.',
        duration: 3000
      }));
    } catch {
      dispatch(addNotification({
        type: 'error',
        title: 'Reprise impossible',
        message: 'Cette conversation est deja geree par l’assistant.',
        duration: 5000
      }));
    }
  };

  const getAgentStatusColor = (agent: any) => {
    if (!agent) return 'default';
    switch (agent.status) {
      case 'active': return 'success';
      case 'training': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  if (!selectedChat) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        flexDirection: 'column',
        gap: 2
      }}>
        <PhoneIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
        <Typography variant="h6" color="text.secondary">
          Sélectionnez une conversation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choisissez une conversation dans la liste pour commencer
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Header de la conversation */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense" sx={{ minHeight: 56 }}>
          <Avatar sx={{ mr: 2 }}>
            {selectedChat.contactName?.[0] || selectedChat.phoneNumber[0]}
          </Avatar>
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              {selectedChat.contactName || selectedChat.phoneNumber}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                label={selectedChat.status}
                color={selectedChat.status === 'active' ? 'success' : 'default'}
                variant="outlined"
              />
              {selectedAgent && (
                <Chip
                  size="small"
                  icon={<BotIcon />}
                  label={selectedAgent.name}
                  color={getAgentStatusColor(selectedAgent)}
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          {/* Statistiques rapides */}
          <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
            <Tooltip title="Messages non lus">
              <Badge badgeContent={selectedChat.unreadCount} color="error">
                <Chip 
                  size="small" 
                  label={`${chatStats.totalMessages} msgs`}
                  variant="outlined"
                />
              </Badge>
            </Tooltip>
            <Tooltip title="Taux de réponse IA">
              <Chip 
                size="small" 
                label={`${Math.round(chatStats.aiResponseRate)}% IA`}
                color="secondary"
                variant="outlined"
              />
            </Tooltip>
          </Box>

          {selectedChat.canResumeAi && (
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={isUpdatingStatus}
              onClick={() => setIsResumeDialogOpen(true)}
            >
              Repasser en IA
            </Button>
          )}

          <IconButton 
            onClick={onSettingsClick}
            size="small"
          >
            <SettingsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Messages */}
      <Box 
        ref={messagesContainerRef}
        sx={{ 
          flex: 1, 
          minHeight: 0,
          overflow: 'auto', 
          p: 1,
          backgroundColor: 'grey.50'
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Erreur lors du chargement des messages
          </Alert>
        )}

        {isLoading && messages.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            gap: 2
          }}>
            <BotIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary">
              Nouvelle conversation
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Commencez à échanger avec ce contact.<br/>
              L'agent IA prendra le relais automatiquement.
            </Typography>
          </Box>
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                isLastMessage={index === messages.length - 1}
                onRetry={handleRetryMessage}
                onCopy={handleCopyMessage}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Bouton scroll vers le bas */}
      {showScrollButton && (
        <Fab
          size="small"
          color="primary"
          onClick={scrollToBottom}
          sx={{
            position: 'absolute',
            bottom: 120,
            right: 24,
            zIndex: 1
          }}
        >
          <ScrollDownIcon />
        </Fab>
      )}

      {/* Zone de saisie */}
      <Paper 
        elevation={3} 
        sx={{ 
          flexShrink: 0,
          p: 2, 
          borderRadius: 0,
          borderTop: 1,
          borderColor: 'divider'
        }}
      >
        {/* Indicateur de typing (futur) */}
        {/* <TypingIndicator /> */}
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <IconButton 
            size="small"
            disabled={isSending}
          >
            <AttachIcon />
          </IconButton>

          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder="Tapez votre message..."
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSending}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3
              }
            }}
          />

          <IconButton
            color="primary"
            disabled={!messageInput.trim() || isSending}
            onClick={handleSendMessage}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark'
              },
              '&:disabled': {
                bgcolor: 'action.disabledBackground'
              }
            }}
          >
            {isSending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )}
          </IconButton>
        </Box>

        {/* Compteur de caractères et infos */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 1 
        }}>
          <Typography variant="caption" color="text.secondary">
            {messageInput.length}/1000 caractères
          </Typography>
          
          {selectedAgent && (
            <Typography variant="caption" color="text.secondary">
              Agent IA: {selectedAgent.name} ({selectedAgent.status})
            </Typography>
          )}
        </Box>
      </Paper>

      <Dialog
        open={isResumeDialogOpen}
        onClose={() => setIsResumeDialogOpen(false)}
        aria-labelledby="resume-ai-dialog-title"
      >
        <DialogTitle id="resume-ai-dialog-title">Repasser en IA ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            L’assistant repondra aux prochains messages du client. Les messages deja recus ne recevront pas de reponse automatique.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsResumeDialogOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            color="success"
            disabled={isUpdatingStatus}
            onClick={handleResumeAi}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatWindow;
